import { push } from "connected-react-router";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import tw from "tailwind-styled-components";
import styled from "styled-components";
import { PencilIcon, MailIcon, ArrowLeftIcon } from "@heroicons/react/solid";
import { Formik } from "formik";
import { isEmpty as _isEmpty } from "lodash-es";
import { useHistory, useLocation } from "react-router";

import {
  deleteHuman,
  updateHuman,
  updateHumanTags,
  commitHuman,
} from "../../actions/humans";

import HumanCircles from "./HumanCircles";
import HumanMentions from "./HumanMentions";
import NewCommentForm from "./NewCommentForm";
import Avatar from "../../components/Avatar";
import { DropdownMenu, DropdownMenuItem } from "../../components/DropdownMenu";
import { getSelectedHumanEmail } from "../../selectors/humanContacts";
import { setHumanContact } from "../../actions/humanContacts";
import Input from "../../components/Input";

const HumanInfoWrapper = tw.div`
flex
bg-white 
p-4 
rounded-lg 
shadow
`;

const HumanNameAndCircleWrapper = tw.div`
flex
flex-col
justify-center
w-full
ml-3
`;

const HumanNameWrapper = styled.p`
  #editIcon {
    display: none;
  }
  &:hover #editIcon {
    display: inline;
  }
`;
const HumanName = tw(HumanNameWrapper)`
text-xl 
font-medium 
text-gray-700 
group-hover:text-gray-900
cursor-pointer
`;

const HumanNameInput = tw(Input)`
h-10 
py-1 
w-3/4 
mb-2
`;

const EmailBtn = tw.a`
inline-flex 
items-center 
justify-center 
h-5
w-5 
rounded-full  
ring-2 
ring-white 
bg-sky-700
text-white
cursor-pointer
m-1
`;

const GoogleSearchBtn = tw.a`
inline-flex 
items-center 
justify-center 
h-5
w-5 
rounded-full  
ring-2 
ring-white 
bg-sky-700
text-white
cursor-pointer
m-1
`;

const BackLink = tw.div`
  lg:hidden
  text-sm
  text-gray-500
  mb-2
  cursor-pointer
  flex
  items-center
  gap-1
  sticky
  top-0
  z-10
  bg-gray-100
  py-2
`;

const HumanNameForm = ({ human, onDone }) => {
  const dispatch = useDispatch();

  const onSubmit = useCallback(
    ({ name }) => {
      const newName = name.trim();
      if (newName && human.name !== newName) {
        dispatch(commitHuman(human));
        dispatch(updateHuman(human.id, { name: newName, modified: true }));
        dispatch(updateHumanTags(human.id, newName));
        if (human.isNew) {
          window.plausible && window.plausible("HumanAdded");
        }
      }
      onDone();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [human, dispatch, onDone]
  );

  return (
    <Formik onSubmit={onSubmit} initialValues={{ name: human.name }}>
      {({ values, handleChange, handleSubmit }) => {
        return (
          <form onBlur={handleSubmit} onSubmit={handleSubmit}>
            <HumanNameInput
              autoFocus
              type="text"
              name="name"
              onChange={handleChange}
              value={values.name}
              onFocus={(e) => {
                e.target.select();
              }}
            />
          </form>
        );
      }}
    </Formik>
  );
};

HumanNameForm.propTypes = {
  human: PropTypes.object.isRequired,
  onDone: PropTypes.func.isRequired,
};

const HumanForm = ({ human }) => {
  const email = useSelector((state) => getSelectedHumanEmail(state, human));
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const [editingName, setEditingName] = useState(false);

  const onDeleteHuman = useCallback(() => {
    if (!window.confirm("Are you sure you want to delete this human?")) {
      return;
    }

    dispatch(push(`/h/`));
    dispatch(deleteHuman(human.id));
  }, [human, dispatch]);

  const onDeleteEmail = useCallback(() => {
    if (
      !window.confirm(
        "Are you sure you want to delete this human's email address?"
      )
    ) {
      return;
    }

    dispatch(setHumanContact({ humanId: human.id, type: "email", value: "" }));
  }, [dispatch, human.id]);

  const getDropdownMenu = useMemo(() => {
    const items = [];
    if (!_isEmpty(email.value)) {
      items.push({ name: "Delete email", onClick: onDeleteEmail });
    }
    items.push({ name: "Delete human", onClick: onDeleteHuman });
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  useEffect(() => {
    if (location.state?.highlightName) {
      setEditingName(true);
      history.replace({
        pathname: location.pathname,
        state: undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, history]);

  return (
    <>
      <BackLink onClick={() => history.push("/h/list")}>
        <ArrowLeftIcon className="h-4 w-4" /> Back to list
      </BackLink>
      <HumanInfoWrapper>
        <div>
          <Avatar human={human} isEditable={true} />
        </div>
        <HumanNameAndCircleWrapper>
          {!editingName && (
            <HumanName
              onClick={() => {
                setEditingName(true);
              }}
            >
              {human?.name}
              <PencilIcon id="editIcon" className="h-4 w-4 ml-1" />
            </HumanName>
          )}
          {editingName && (
            <HumanNameForm
              human={human}
              onDone={() => {
                setEditingName(false);
              }}
            />
          )}
          <HumanCircles human={human} />
          {human && <HumanMentions className="mt-2" human={human} />}
          <div className="flex items-center">
            {!_isEmpty(email.value) && (
              <EmailBtn href={`mailto:${email.value}`}>
                <MailIcon className="h-3 w-3" />
              </EmailBtn>
            )}
            {human?.name && (
              <GoogleSearchBtn
                href={`https://www.google.com/search?q=${encodeURIComponent(
                  human.name
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Search on Google"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="h-3 w-3"
                  fill="currentColor"
                >
                  <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z" />
                </svg>
              </GoogleSearchBtn>
            )}
          </div>
        </HumanNameAndCircleWrapper>
        {!human.isNew && (
          <DropdownMenu
            menuButton={<div style={{ fontSize: "25px" }}>...</div>}
            menuItems={getDropdownMenu}
          >
            {getDropdownMenu.map((item, key) => {
              return (
                <DropdownMenuItem key={key}>
                  <p
                    onClick={item.onClick}
                    className={
                      "block px-4 text-left py-2 text-sm text-gray-700 cursor-pointer"
                    }
                  >
                    {item.name}
                  </p>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenu>
        )}
      </HumanInfoWrapper>

      <div className="my-2">
        <NewCommentForm human={human} />
      </div>
    </>
  );
};

HumanForm.propTypes = {
  human: PropTypes.object,
};

export default HumanForm;
